import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Fab from '@mui/material/Fab';
import { useState, useEffect } from 'react';
import { IconFileDownload } from '@tabler/icons';
import TextField from '@mui/material/TextField';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Autocomplete, Button, Popover, Typography } from '@mui/material';
import { handleCatchError } from 'utils/CatchHandler';
import { superdistLeadlist, creditLeadCommission } from 'utils/API/Superdistributor APIs/SuperdistributorApiCalls';
import { globalNotifySuccess } from 'views/utilities/AlertToast';
import BreadcrumbComponent from 'views/Components/BreadCrumb';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import de from 'date-fns/locale/en-GB';
import { format } from 'date-fns';

const ListGoldLoanLeads = () => {
    const [rows, setRows] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [rowCount, setRowCount] = useState(0);
    const [fromDate, setFromDate] = useState(null);
    const [toDate, setToDate] = useState(null);
    const [status, setStatus] = useState(null);

    // Popover State
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedRow, setSelectedRow] = useState(null);
    const [creditAmount, setCreditAmount] = useState('');
    const [popoverError, setPopoverError] = useState('');

    const statusOptions = ['Approved', 'Pending', 'Rejected'];

    const handleExcelDownload = () => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Gold Loan Leads');
        const headerRow = worksheet.addRow(columns.map((column) => column?.headerName));
        headerRow.font = { bold: true };

        filteredData.forEach((rowData) => {
            const dataRow = worksheet.addRow(columns.map((column) => rowData[column?.field]));
            dataRow.alignment = { vertical: 'middle', horizontal: 'left' };
        });

        workbook.xlsx.writeBuffer().then((buffer) => {
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            saveAs(blob, 'GoldLoanLeads.xlsx');
        });
    };

    const columns = [
        {
            field: 'created_at',
            headerName: 'Date',
            width: 180,
            valueGetter: (params) => {
                if (!params.value) return 'N/A';
                const options = { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Asia/Kolkata' };
                const indianDate = new Date(params.value).toLocaleDateString('en-GB', options);
                const indianTime = new Date(params.value).toLocaleTimeString('en-US', { timeZone: 'Asia/Kolkata' });
                return `${indianDate} ${indianTime}`;
            }
        },
        {
            field: 'amount',
            headerName: 'Amount',
            width: 150,
            valueGetter: (params) => params.value || 'N/A'
        },
        {
            field: 'approved_amount',
            headerName: 'Approved Amount',
            width: 150,
            valueGetter: (params) => params.value || 'N/A'
        },
        {
            field: 'firstname',
            headerName: 'First Name',
            width: 150,
            valueGetter: (params) => params.value || 'N/A'
        },
        {
            field: 'lastname',
            headerName: 'Last Name',
            width: 150,
            valueGetter: (params) => params.value || 'N/A'
        },
        {
            field: 'mobileno',
            headerName: 'Mobile',
            width: 130,
            valueGetter: (params) => params.value || 'N/A'
        },
        {
            field: 'email',
            headerName: 'Email',
            width: 200,
            valueGetter: (params) => params.value || 'N/A'
        },
        {
            field: 'pincode',
            headerName: 'Pincode',
            width: 100,
            valueGetter: (params) => params.value || 'N/A'
        },
        {
            field: 'refid',
            headerName: 'Ref. ID',
            width: 130,
            valueGetter: (params) => params.value || 'N/A'
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            valueGetter: (params) => params.value || 'N/A'
        }
        // {
        //     field: 'actions',
        //     headerName: 'Actions',
        //     width: 150,
        //     renderCell: (params) => {
        //         if (
        //             params.row.sd_commission_iscredit === 'CREDITED' &&
        //             params.row.sd_commission &&
        //             !params.row.customer_commission_iscredit
        //         ) {
        //             return (
        //                 <Button variant="outlined" size="small" onClick={(event) => handleOpenPopover(event, params.row)}>
        //                     Credit
        //                 </Button>
        //             );
        //         }
        //         return null;
        //     }
        // }
    ];

    const fetchLeads = async (pageNumber, size) => {
        try {
            setIsLoading(true);
            const payload = {
                pageNumber: pageNumber + 1, // API usually expects 1-based index
                pageSize: size,
                fromdate: fromDate ? format(new Date(fromDate), 'yyyy-MM-dd') : '',
                todate: toDate ? format(new Date(toDate), 'yyyy-MM-dd') : '',
                status: status || ''
            };
            const response = await superdistLeadlist(payload);
            setIsLoading(false);
            if (response?.status === 200) {
                const dataWithIds = response.data.data.map((item, index) => ({
                    ...item,
                    id: item.id || index // Fallback to index if no ID
                }));
                setRows(dataWithIds);
                // If API provides total count, use it. Otherwise, we might default to server-side pagination assumption
                // For now, if total is not sent, we might not be able to paginate correctly beyond the first page if we rely strictly on server.
                // Assuming standard response might have 'total' or 'count'. If not, we set it to current rows length temporarily or handle it.
                // Assuming response.data.total exists for pagination
                setRowCount(response?.data?.total || dataWithIds.length);
            } else {
                setRows([]);
                setRowCount(0);
            }
        } catch (error) {
            setIsLoading(false);
            handleCatchError(error);
            console.log(error.message);
        }
    };

    useEffect(() => {
        fetchLeads(page, pageSize);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, pageSize]);

    const handleFilter = () => {
        setPage(0); // Reset to first page on filter
        fetchLeads(0, pageSize);
    };

    // const handleOpenPopover = (event, row) => {
    //     setAnchorEl(event.currentTarget);
    //     setSelectedRow(row);
    //     setCreditAmount('');
    //     setPopoverError('');
    // };

    const handleClosePopover = () => {
        setAnchorEl(null);
        setSelectedRow(null);
        setCreditAmount('');
        setPopoverError('');
    };

    const handleCreditSubmit = async () => {
        if (!creditAmount) {
            setPopoverError('Please enter an amount');
            return;
        }

        const maxAmount = parseFloat(selectedRow?.sd_commission || 0);
        const enteredAmount = parseFloat(creditAmount);

        if (isNaN(enteredAmount) || enteredAmount <= 0) {
            setPopoverError('Please enter a valid amount');
            return;
        }

        if (enteredAmount > maxAmount) {
            setPopoverError(`Amount cannot exceed ${maxAmount}`);
            return;
        }

        setIsLoading(true);
        const payload = {
            id: selectedRow.id,
            commission: enteredAmount
        };
        const response = await creditLeadCommission(payload);
        setIsLoading(false);
        if (response?.status === 200) {
            globalNotifySuccess(response?.data?.message || 'Commission credited successfully');
            handleClosePopover();
            fetchLeads(page, pageSize); // Refresh list
        }
    };

    const openPopover = Boolean(anchorEl);
    const popoverId = openPopover ? 'simple-popover' : undefined;

    const breadCrumbData = [
        {
            label: 'Gold Loan Leads',
            path: ''
        }
    ];
    return (
        <>
            <BreadcrumbComponent routes={breadCrumbData} />
            <Box
                sx={{
                    width: '100%',
                    height: 'auto',
                    backgroundColor: '#fff',
                    m: 0,
                    borderRadius: 2,
                    padding: '20px 10px',
                    '@media screen and (min-width: 600px)': {
                        padding: 5
                    }
                }}
            >
                <Box sx={{ flexGrow: 1, pb: 1 }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                                <DatePicker
                                    label="From Date"
                                    value={fromDate}
                                    onChange={(newValue) => setFromDate(newValue)}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={de}>
                                <DatePicker
                                    label="To Date"
                                    value={toDate}
                                    onChange={(newValue) => setToDate(newValue)}
                                    slotProps={{ textField: { size: 'small', fullWidth: true } }}
                                />
                            </LocalizationProvider>
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={2}>
                            <Autocomplete
                                options={statusOptions}
                                value={status}
                                onChange={(event, newValue) => setStatus(newValue)}
                                renderInput={(params) => <TextField {...params} label="Status" size="small" />}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6} md={3} lg={1}>
                            <Button variant="contained" onClick={handleFilter}>
                                Filter
                            </Button>
                        </Grid>
                        <Grid item xs={3} sm={2} lg={2}>
                            <Fab onClick={handleExcelDownload} variant="extended" color="primary" size="small" aria-label="add">
                                <IconFileDownload sx={{ mr: 1 }} />
                                Excel
                            </Fab>
                        </Grid>
                    </Grid>
                </Box>

                <div style={{ height: 600, width: '100%' }}>
                    <DataGrid
                        rows={rows} // Use rows directly to support server-side data which is already filtered/paginated
                        columns={columns}
                        rowCount={rowCount}
                        loading={isLoading}
                        pageSizeOptions={[10, 20, 50]}
                        paginationModel={{ page, pageSize }}
                        paginationMode="server"
                        onPaginationModelChange={(params) => {
                            setPage(params.page);
                            setPageSize(params.pageSize);
                        }}
                        slots={{ toolbar: GridToolbar }}
                        disableSelectionOnClick
                    />
                </div>
            </Box>
            <Popover
                id={popoverId}
                open={openPopover}
                anchorEl={anchorEl}
                onClose={handleClosePopover}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
            >
                <Box sx={{ p: 2, width: 300 }}>
                    <Typography variant="h6" gutterBottom>
                        Credit Commission
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        Max Amount: {selectedRow?.sd_commission}
                    </Typography>
                    <TextField
                        label="Amount"
                        type="number"
                        fullWidth
                        size="small"
                        value={creditAmount}
                        onChange={(e) => setCreditAmount(e.target.value)}
                        error={!!popoverError}
                        helperText={popoverError}
                        sx={{ mb: 2, mt: 1 }}
                    />
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button onClick={handleClosePopover} size="small">
                            Cancel
                        </Button>
                        <Button variant="contained" onClick={handleCreditSubmit} size="small">
                            Submit
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

export default ListGoldLoanLeads;
